import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  HandCoins, 
  CheckCircle2, 
  ArrowRight, 
  Wrench, 
  Calendar, 
  Shield,
  Phone,
  Car
} from "lucide-react";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import CarsPage from "./pages/CarsPage";
import CarDetailPage from "./pages/CarDetailPage";
import SellYourCarPage from "./pages/SellYourCarPage";
import RepairsPage from "./pages/RepairsPage";
import ContactPage from "./pages/ContactPage";
import DashboardPage from "./pages/DashboardPage";
import DashboardLoginPage from "./pages/DashboardLoginPage";
import DashboardAddCarPage from "./pages/DashboardAddCarPage";
import DashboardCarsPage from "./pages/DashboardCarsPage";
import DashboardEditCarPage from "./pages/DashboardEditCarPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Featured Cars Component
const FeaturedCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCars = async () => {
      try {
        // Only fetch cars that are featured=true AND status=available, limit to 3
        const response = await axios.get(`${API}/cars?featured=true&status=available&page_size=3`);
        setCars(response.data.cars || []);
      } catch (e) {
        console.error("Failed to fetch featured cars:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedCars();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="aspect-[4/3] bg-[#16202A] animate-pulse" />
        ))}
      </div>
    );
  }

  if (cars.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {cars.map((car, index) => (
        <motion.div
          key={car.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <Link
            to={`/cars/${car.id}`}
            className="group block bg-[#16202A]/50 border border-white/10 overflow-hidden hover:border-[#48CCCD]/30 transition-all duration-300"
            data-testid={`featured-car-${index}`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={car.images?.[0] || "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800"}
                alt={`${car.make} ${car.model}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-4">
              <h3 className="font-serif text-lg font-bold text-[#F8FAFC] mb-1">
                {car.make} {car.model}
              </h3>
              <p className="text-xs text-[#94A3B8] font-sans mb-2">
                {car.year} • {car.mileage?.toLocaleString()} miles • {car.fuel_type}
              </p>
              <p className="font-serif text-xl font-bold text-[#48CCCD]">
                €{car.price?.toLocaleString()}
              </p>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  );
};

const Home = () => {
  const [healthStatus, setHealthStatus] = useState(null);

  const checkHealth = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setHealthStatus(response.data);
      console.log("Health check:", response.data);
    } catch (e) {
      console.error(e, "Health check failed");
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const weBuyCarsFeatures = [
    'Fast, no-obligation valuation',
    'Fair, transparent offers',
    'We handle all paperwork',
    'Same-day payment available',
  ];

  const appointmentServices = [
    {
      icon: Car,
      title: 'View & Test Drive',
      description: 'Come see our vehicles in person and take them for a spin.',
    },
    {
      icon: Shield,
      title: 'NCT Prep',
      description: 'Professional vehicle preparation for NCT inspection.',
    },
    {
      icon: Calendar,
      title: 'Servicing & Repairs',
      description: 'From routine maintenance to repairs, we have you covered.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#020405] noise-overlay" data-testid="home-page">
      <Header />
      <main>
        <Hero />
        
        {/* Featured Cars Section */}
        <section 
          className="py-20 md:py-28 bg-[#020405]"
          data-testid="featured-cars-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12"
            >
              <div>
                <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-4">
                  Our Inventory
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] tracking-tight">
                  Featured Vehicles
                </h2>
              </div>
              <Link
                to="/cars"
                className="inline-flex items-center gap-2 text-[#48CCCD] hover:text-white transition-colors text-sm font-sans uppercase tracking-wider group"
                data-testid="view-all-cars-link"
              >
                View All Cars
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            
            <FeaturedCars />
          </div>
        </section>

        {/* We Buy Cars Section */}
        <section 
          className="py-20 md:py-28 bg-[#0A0F14]"
          data-testid="we-buy-cars-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative order-2 lg:order-1"
              >
                <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cardealer-cmac/artifacts/5aa8qcw6_e15b3f7b-43af-4b56-8fba-3daa68d88847.JPG"
                    alt="We buy cars"
                    className="w-full h-full object-cover"
                    data-testid="we-buy-cars-image"
                  />
                  {/* Subtle right edge fade */}
                  <div className="absolute inset-0 bg-gradient-to-l from-[#0A0F14]/40 via-transparent to-transparent pointer-events-none" />
                </div>
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -left-4 w-full h-full border border-[#48CCCD]/20 rounded-2xl -z-10" />
                <div className="absolute top-4 right-4 bg-[#48CCCD] text-black px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider">
                  Instant Offers
                </div>
              </motion.div>
              
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                    <HandCoins size={20} className="text-[#48CCCD]" />
                  </div>
                  <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em]">
                    We Buy Cars
                  </p>
                </div>
                
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6">
                  Selling Your Car?
                  <br />
                  <span className="text-[#94A3B8]">We Make It Easy</span>
                </h2>
                
                <p className="font-sans text-[#94A3B8] text-base leading-relaxed mb-8">
                  Looking to sell? Get a fast, fair, no-obligation offer from a trusted local dealership. 
                  We buy all makes and models—no matter the age or condition.
                </p>
                
                {/* Benefits */}
                <ul className="space-y-3 mb-8">
                  {weBuyCarsFeatures.map((feature, index) => (
                    <motion.li
                      key={feature}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-3 text-[#F8FAFC] text-sm font-sans"
                    >
                      <CheckCircle2 size={18} className="text-[#48CCCD] shrink-0" />
                      {feature}
                    </motion.li>
                  ))}
                </ul>
                
                <Link
                  to="/sell-your-car"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors duration-300"
                  data-testid="we-buy-cars-cta"
                >
                  <HandCoins size={18} />
                  Get an Offer for Your Car
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Appointments Section */}
        <section 
          id="repairs" 
          className="py-20 md:py-28 bg-[#020405]"
          data-testid="repairs-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Calendar size={20} className="text-[#48CCCD]" />
                </div>
              </div>
              <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-4">
                Appointments
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-4">
                Book Your Visit
              </h2>
              <p className="font-sans text-[#94A3B8] text-base max-w-2xl mx-auto">
                Whether you want to view a car, arrange a test drive, or book in for servicing — 
                we are here to help.
              </p>
            </motion.div>
            
            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {appointmentServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden bg-[#16202A]/50 border border-white/10 p-8 hover:border-[#48CCCD]/30 transition-all duration-500"
                  data-testid={`repair-service-${index}`}
                >
                  <div className="relative z-10">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0A0F14] mb-6">
                      <service.icon size={24} className="text-[#48CCCD]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#F8FAFC] mb-3">
                      {service.title}
                    </h3>
                    <p className="font-sans text-sm text-[#94A3B8] leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  {/* Hover gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#48CCCD]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              ))}
            </div>
            
            {/* Appointment CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link
                to="/repairs"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors duration-300"
                data-testid="repairs-cta"
              >
                <Calendar size={18} />
                Book an Appointment
              </Link>
            </motion.div>
          </div>
        </section>

        {/* About Section */}
        <section 
          id="about" 
          className="py-20 md:py-28 bg-[#0A0F14]"
          data-testid="about-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Text Content */}
              <div>
                <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-4">
                  About Us
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6">
                  A Legacy of
                  <br />
                  <span className="text-[#94A3B8]">Automotive Excellence</span>
                </h2>
                <p className="font-sans text-[#94A3B8] text-base leading-relaxed mb-8">
                  At CMAC & Son Autos, we believe in delivering more than just vehicles—we deliver 
                  peace of mind. Every car in our collection is meticulously inspected and prepared 
                  to meet the highest standards of quality and reliability.
                </p>
                <div className="flex gap-8">
                  <div>
                    <p className="font-serif text-3xl font-bold text-[#48CCCD]">—</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-sans mt-2">
                      Quality Assured
                    </p>
                  </div>
                  <div>
                    <p className="font-serif text-3xl font-bold text-[#48CCCD]">—</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-sans mt-2">
                      Customer First
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cardealer-cmac/artifacts/zujgtprv_6217dd0e-e3db-44af-a65c-ddba3c78413f.JPG"
                    alt="Premium car interior"
                    className="w-full h-full object-cover"
                    data-testid="about-image"
                  />
                  {/* Subtle left edge fade */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F14]/40 via-transparent to-transparent pointer-events-none" />
                </div>
                {/* Decorative border */}
                <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#48CCCD]/20 rounded-2xl -z-10" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cars" element={
            <>
              <Header />
              <CarsPage />
              <Footer />
            </>
          } />
          <Route path="/cars/:id" element={
            <>
              <Header />
              <CarDetailPage />
              <Footer />
            </>
          } />
          <Route path="/sell-your-car" element={
            <>
              <Header />
              <SellYourCarPage />
              <Footer />
            </>
          } />
          <Route path="/repairs" element={
            <>
              <Header />
              <RepairsPage />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Header />
              <ContactPage />
              <Footer />
            </>
          } />
          <Route path="/dashboard/login" element={
            <>
              <Header />
              <DashboardLoginPage />
              <Footer />
            </>
          } />
          <Route path="/dashboard" element={
            <>
              <Header />
              <DashboardPage />
              <Footer />
            </>
          } />
          <Route path="/dashboard/cars/new" element={
            <>
              <Header />
              <DashboardAddCarPage />
              <Footer />
            </>
          } />
          <Route path="/dashboard/cars/:id/edit" element={
            <>
              <Header />
              <DashboardEditCarPage />
              <Footer />
            </>
          } />
          <Route path="/dashboard/cars" element={
            <>
              <Header />
              <DashboardCarsPage />
              <Footer />
            </>
          } />
          <Route path="/privacy-policy" element={
            <>
              <Header />
              <PrivacyPolicyPage />
              <Footer />
            </>
          } />
          <Route path="/terms" element={
            <>
              <Header />
              <TermsPage />
              <Footer />
            </>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
